if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            
            // Get all product images from the fetched page
            const allImages = responseHTML.querySelectorAll('.product__media-wrapper .product__media img');
            const imageUrls = [];
            allImages.forEach(img => {
              const src = img.getAttribute('src');
              if (src && !imageUrls.includes(src)) {
                imageUrls.push(src);
              }
            });

            this.preprocessHTML(productElement);
            
            // Build custom gallery HTML
            let galleryHTML = '';
            if (imageUrls.length > 0) {
              galleryHTML = `
                <div class="quick-add-gallery">
                  <div class="quick-add-gallery__main">
                    <img src="${imageUrls[0]}" alt="Product image" class="quick-add-gallery__main-image" />
                  </div>
                  ${imageUrls.length > 1 ? `
                    <div class="quick-add-gallery__thumbnails">
                      ${imageUrls.map((url, index) => `
                        <button class="quick-add-gallery__thumb ${index === 0 ? 'is-active' : ''}" data-index="${index}" data-src="${url}">
                          <img src="${url}" alt="Thumbnail ${index + 1}" />
                        </button>
                      `).join('')}
                    </div>
                  ` : ''}
                </div>
              `;
            }
            
            // Create the full modal content
            const modalHTML = `
              <div class="quick-add-modal__with-gallery">
                ${galleryHTML}
                <div class="quick-add-modal__product-info">
                  ${productElement.outerHTML}
                </div>
              </div>
            `;
            
            HTMLUpdateUtility.setInnerHTML(this.modalContent, modalHTML);
            
            // Initialize thumbnail click handlers
            this.initGalleryThumbnails();
            
            // Initialize size chart modal functionality
            this.initSizeChartModal();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }
      
      initGalleryThumbnails() {
        const thumbnails = this.modalContent.querySelectorAll('.quick-add-gallery__thumb');
        const mainImage = this.modalContent.querySelector('.quick-add-gallery__main-image');
        
        if (!mainImage || thumbnails.length === 0) return;
        
        thumbnails.forEach(thumb => {
          thumb.addEventListener('click', () => {
            // Update main image
            mainImage.src = thumb.dataset.src;
            
            // Update active state
            thumbnails.forEach(t => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
          });
        });
      }
      
      initSizeChartModal() {
        // Find the size chart modal opener and dialog within the quick-add modal
        // Support both old ID format (PopupModal-custom-id) and new format (PopupModal-size-chart-*)
        const sizeChartOpener = this.modalContent.querySelector('[data-modal*="PopupModal-size-chart"], [data-modal="#PopupModal-custom-id"]');
        const sizeChartDialog = this.modalContent.querySelector('[id*="PopupModal-size-chart"], #PopupModal-custom-id');
        
        if (!sizeChartOpener || !sizeChartDialog) return;
        
        const openButton = sizeChartOpener.querySelector('button');
        const closeButton = sizeChartDialog.querySelector('[id^="ModalClose"]');
        
        if (openButton) {
          openButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            sizeChartDialog.setAttribute('open', '');
            sizeChartDialog.style.display = 'block';
            // Add overlay/backdrop if needed
            document.body.classList.add('overflow-hidden');
          });
        }
        
        if (closeButton) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            sizeChartDialog.removeAttribute('open');
            sizeChartDialog.style.display = 'none';
            document.body.classList.remove('overflow-hidden');
          });
        }
        
        // Close on backdrop click
        sizeChartDialog.addEventListener('click', (e) => {
          if (e.target === sizeChartDialog) {
            sizeChartDialog.removeAttribute('open');
            sizeChartDialog.style.display = 'none';
            document.body.classList.remove('overflow-hidden');
          }
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        // Remove modal dialogs EXCEPT the size chart modal
        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) {
          modalDialog.forEach((modal) => {
            // Keep the size chart modal (PopupModal-custom-id or PopupModal-size-chart-*)
            if (!modal.id || (!modal.id.includes('PopupModal-custom-id') && !modal.id.includes('PopupModal-size-chart'))) {
              modal.remove();
            }
          });
        }
        
        // Remove the media wrapper since we're showing our custom gallery
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (mediaWrapper) mediaWrapper.remove();
        
        // Remove any existing product grid layout classes
        const productGrid = productElement.querySelector('.product');
        if (productGrid) {
          productGrid.classList.remove('grid', 'grid--1-col', 'grid--2-col-tablet');
          productGrid.style.display = 'block';
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        
        // First, mark the size chart modal to preserve its ID
        const sizeChartModal = productElement.querySelector('#PopupModal-custom-id');
        const sizeChartOpener = productElement.querySelector('[data-modal="#PopupModal-custom-id"]');
        
        // Replace IDs in the HTML
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}